// @Library
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EditorState } from 'draft-js';
import { convertToHTML } from 'draft-convert';
import toast from 'react-hot-toast';

// @Models
import { IBudgetComments } from '../../../models';

// @Utils
import { axiosEstimates } from '../../../utils/api';
import { socket } from '../../../utils/sockets.util';

// @Redux
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/reducers';

interface IParams {
  open: boolean;
  idBudget?: number;
}

interface IFiles {
  idUser: number;
  filename: string;
  url: string;
}

interface RequestData {
  comments: IBudgetComments[];
  budget: {
    status: string;
  };
}

export function useComments({ open, idBudget }: IParams) {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [comments, setComments] = useState<IBudgetComments[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<string>('active');
  const [savedFiles, setSavedFiles] = useState<IFiles[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t, i18n } = useTranslation(['common']);

  const {
    user: { idCRM },
  } = useSelector((state: RootState) => state);
  const idExports = useSelector((state: RootState) => state.user.user?.id);

  const getComments = async () => {
    try {
      if (idBudget) {
        const comments = await axiosEstimates.get<RequestData>('comments/' + idBudget);
        setComments(comments.data.comments);
        setStatus(comments.data.budget.status);
      }
    } catch (error) {
      console.error('[comments] getComments error', error);
      setComments([]);
    }
  };

  const getFiles = async () => {
    try {
      if (idBudget) {
        const files = await axiosEstimates.get<IFiles[]>('file-comments/' + idBudget);
        setSavedFiles(files?.data);
      }
    } catch (error) {
      console.error('[comments] getFiles error', error);
      setSavedFiles([]);
    }
  };

  const updateComments = async () => {
    await getComments();
    await getFiles();
  };

  useEffect(() => {
    if (open) {
      getComments();
      getFiles();
      setEditorState(EditorState.createEmpty());
    }

    return () => {
      setComments([]);
    };
  }, [open]);

  const handleCommentsSuccess = (data: any) => {
    console.log('[comments] sendComments:success', data);
    setIsSubmitting(false);
    toast.success(t('toast.sent'));

    try {
      // Si el backend envía los comentarios completos, usarlos
      if (data?.comments && Array.isArray(data.comments)) {
        setComments(data.comments);
      } else if (data?.newComment) {
        // Transformar el comentario del socket al formato esperado
        const newComment: IBudgetComments = {
          id: data.newComment.id,
          comment: data.newComment.comment,
          date: data.newComment.created_at,
          idBudget: Number.parseInt(data.newComment.idBudget),
          idUser: Number.parseInt(data.newComment.idUser),
          user: data.username || 'Usuario',
        };

        // Agregar el nuevo comentario al estado actual
        setComments(prevComments => {
          // Verificar que no esté duplicado
          const exists = prevComments.some(comment => comment.id === newComment.id);
          if (exists) {
            return prevComments;
          }
          return [...prevComments, newComment];
        });
      } else if (data && Array.isArray(data)) {
        // Si envía un array de comentarios directamente
        setComments(data);
      } else {
        // Fallback: actualizar desde API
        updateComments();
      }
    } catch (error) {
      console.error('Error processing socket comment data:', error);
      updateComments(); // Fallback en caso de error
    }
  };

  useEffect(() => {
    const handleCommentsError = (error: unknown) => {
      console.error('[comments] sendComments:error', error);
      setIsSubmitting(false);
      toast.error(t('toast.error'));
    };

    socket.on('sendComments:success', handleCommentsSuccess);
    socket.on('sendComments:error', handleCommentsError);

    return () => {
      socket.off('sendComments:success', handleCommentsSuccess);
      socket.off('sendComments:error', handleCommentsError);
    };
  }, []);

  const handleEditorChange = (value: EditorState) => {
    setEditorState(value);
  };

  const handleSubmit = async () => {
    if (!idBudget || !idCRM || status === 'cancel' || isSubmitting) return null;

    const comment = convertToHTML(editorState.getCurrentContent());
    if (comment === '<p></p>') return toast.error(t('comments.required'));
    const lang =
      new URLSearchParams(globalThis.location.search).get('lang') ||
      globalThis.localStorage.getItem('lang') ||
      i18n.language ||
      'es';

    const data = {
      idBudget,
      idCRM,
      idUser: idCRM,
      idSeller: idCRM,
      comment,
      files,
      idExports,
      lang,
    };

    setIsSubmitting(true);
    console.log('[comments] emit sendComments', data);
    socket.emit('sendComments', data, (ack: unknown) => {
      console.log('[comments] sendComments:ack', ack);
    });
    setEditorState(EditorState.createEmpty());
    setFiles([]);
  };

  return { comments, t, editorState, handleEditorChange, handleSubmit, files, setFiles, savedFiles, status };
}
